import { additionalMiddleCategory, freightMiddleCategory, surChargeMiddleCategory } from 'data/quote';
import { useEffect, useState } from 'react';
import { ISubData, ITotal } from 'types/quote';
import { ISelectOption } from '../CustomSelect';

interface IQuoteDetail {
    title: string
    list: ISubData[]
    type: number
}

const QuoteDetailTable = ({ title, list, type }: IQuoteDetail) => {
    const [total, setTotal] = useState<ITotal[]>([]);
    const tHeader = [
        { label: '항목', flex: 1 },
        { label: '단가', flex: 1, isRight: true },
        { label: '수량 / 단위', flex: 1 },
        { label: '계약통화기준', flex: 1 },
        { label: '금액', flex: 1, isRight: true },
    ]

    useEffect(() => {
        if (list) {
            setTotal(calcCurrency(list))
        }
    }, [list])

    const calcCurrency = (list: ISubData[]): ITotal[] => {
        const map = list.reduce((acc, item) => {
            const { currency, price } = item;
            if (!currency || price == null || isNaN(price)) return acc; // 유효성 체크
            acc.set(currency, (acc.get(currency) || 0) + Number(price));
            return acc;
        }, new Map<string, number>());

        const priority = ['USD', 'KRW', 'EUR'];

        return Array.from(map, ([currency, price]) => ({ currency, price }))
            .sort((a, b) => {
                const indexA = priority.indexOf(a.currency.toUpperCase());
                const indexB = priority.indexOf(b.currency.toUpperCase());
                return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
            }).filter(arr => !arr.currency.includes('Cost'))
    };

    const convertChargeName = (chargeName: string) => {
        const filered: ISelectOption[] = (type === 1 ? freightMiddleCategory : type === 2 ? surChargeMiddleCategory : additionalMiddleCategory).filter(arr => arr.value === chargeName)
        return filered[0]?.label ?? ''
    }

    const tBodyStyle = () => {
        return `flex items-center justify-center py-2 text-center tablet:py-0 tablet:gap-2`
    }

    const titleStyle = () => {
        return `hidden tablet:block flex-[1.0] w-full bg-[#F8F8FA] py-1`
    }

    return (
        <>
            {
                list.length > 0 &&
                <div className='flex flex-col gap-3 p-10 tablet:p-4 border border-[#9D9EB7] rounded-lg'>
                    <p className='font-semibold text-lg'>{title}</p>

                    <div className='tablet:hidden flex justify-between items-center gap-6 bg-[#F8F8F8] px-6 py-4 border-black font-semibold'>
                        {
                            tHeader.map((item, idx) => (
                                <p className={`flex-[1] ${item.isRight ? 'text-right' : ''}`} key={idx}>{item.label}</p>
                            ))
                        }
                    </div>

                    <div className='flex flex-col gap-3 pb-3 border-b border-black'>
                        {
                            list.map((item, idx) => (
                                <div className='flex tablet:flex-col justify-between md:items-center tablet:items-stretch gap-6 tablet:gap-1 tablet:p-0 px-6 py-4 border rounded' key={idx}>
                                    <div className={`flex-[1] ${tBodyStyle()}`}>
                                        <p className={titleStyle()}>{tHeader[0].label}</p>
                                        <p className='flex-1 text-left tablet:text-center'>
                                            {convertChargeName(item.chargeName)} <br />{item.chargeNameDetail}
                                        </p>
                                    </div>
                                    <div className={`flex-[1] ${tBodyStyle()}`}>
                                        <p className={titleStyle()}>{tHeader[1].label}</p>
                                        <p className='flex-1'>
                                            {
                                                item.currency.includes('Cost') ?
                                                    `${item.currency}` :
                                                    <>
                                                        {item.currency} {Number(item.unitPrice)?.toLocaleString()}
                                                    </>
                                            }
                                        </p>
                                    </div>
                                    <div className={`flex-[1] ${tBodyStyle()}`}>
                                        <p className={titleStyle()}>{tHeader[2].label}</p>
                                        <p className='flex-1 text-left tablet:text-center'>
                                            {
                                                item.currency.includes('Cost') ?
                                                    '-' :
                                                    <>
                                                        x {item.quantity} {item.unit}
                                                    </>
                                            }
                                        </p>
                                    </div>
                                    <div className={`flex-[1] ${tBodyStyle()}`}>
                                        <p className={titleStyle()}>{tHeader[3].label}</p>
                                        <p className='flex-1 text-left tablet:text-center'>
                                            {
                                                item.currency.includes('Cost') ?
                                                    '-' :
                                                    <>
                                                        {item.currency} {Number(item.unitPrice)?.toLocaleString()}
                                                    </>
                                            }
                                        </p>
                                    </div>
                                    <div className={`flex-[1] ${tBodyStyle()}`}>
                                        <p className={titleStyle()}>{tHeader[4].label}</p>
                                        <p className='flex-1 tablet:text-center text-right'>
                                            {
                                                item.currency.includes('Cost') ?
                                                    '-' :
                                                    <>
                                                        {item.currency} {item.price?.toLocaleString()}
                                                    </>
                                            }
                                        </p>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                    <div className='flex justify-between items-center font-semibold tablet:text-base text-lg'>
                        <p>소계</p>
                        <div className='flex gap-4'>
                            {
                                total.map((item, idx) => (
                                    <div
                                        className='flex gap-2 font-semibold tablet:text-base text-lg'
                                        key={idx}>
                                        <p>{item.currency}</p>
                                        <p>{item.price?.toLocaleString()}</p>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                </div>
            }
        </>
    );
};

export default QuoteDetailTable;